import React, { Fragment, useState } from 'react'
import {
  Box,
  Typography,
  Tabs,
  FormControl,
  TextField,
  IconButton,
  FormHelperText,
  MenuItem,
  Select,
  Tab,
  Divider
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useTranslation } from 'react-i18next'
/** Redux */
import { useSelector } from 'react-redux'

import {
  elementTypes,
  getFirstKeyFromObject,
  elements
} from '../../utils/formBuilder'
import { modalFormStyles } from '../../styles/classes/FieldFormClasses'
import { leftPanelStyles } from '../../styles/classes/FormBuilderClasses'
import TabPanel from './TabPanel'
import AuditTrailTab from './AuditTrailTab'
import { menuProps } from '../../styles/mui_custom_theme'
import DraggableElement from './DraggableElement'
import StructureTab from './StructureTab'
import { useDroppable } from '@dnd-kit/core'
import { statusOptions } from '../../utils/fieldForms'
import Element from './Element'
import { mcs360 } from '../../lib/Constants'

const LeftPanel = ({
  status,
  formDescription,
  onFormDescriptionChange,
  trade,
  service,
  woType,
  tradeList,
  serviceList,
  typeList,
  onTradeChange,
  onServiceChange,
  onWoTypeChange,
  onClickAdd,
  draggableComponents,
  configurationsField,
  onAddSection,
  onEditSectionName,
  onRemoveSection,
  activeId,
  extraConditions
}) => {
  const user = useSelector(state => state.auth.user)
  const elementsConfigs =
    user?.userInfo?.configurations?.portals?.management?.elements
  const userStore = useSelector(state => state.auth.user)
  let disabledElements = []
  if (
    userStore?.userInfo?.parent_subdomain === mcs360
  ) {
    disabledElements = [elementTypes.LINE_ITEMS, elementTypes.MATERIAL_PARTS]
  }

  const { classes } = leftPanelStyles()
  const modalClasses = modalFormStyles().classes
  const [showAssignmentTip, setShowAssignmentTip] = useState(false)
  const [leftTabValue, setLeftTabValue] = useState(0)
  const { t } = useTranslation()
  const publishedMode = status === statusOptions.PUBLISHED
  const TRASH_ID = 'void'
  const isDraggingMiddle =
    !!activeId &&
    !draggableComponents.find(item => getFirstKeyFromObject(item) === activeId)
  const handleLeftTabChange = (event, newValue) => {
    setLeftTabValue(newValue)
  }

  const helpTipHandler = () => {
    setShowAssignmentTip(prevState => !prevState)
  }

  const Trash = ({ id, children }) => {
    const { setNodeRef } = useDroppable({
      id
    })
    return (
      <div
        className={`${classes.elementsTabContainer} ${
          isDraggingMiddle && classes.elementsTabContainerRedBorder
        }`}
        ref={setNodeRef}
      >
        {children}
      </div>
    )
  }

  const details = () => {
    return (
      <Box className={classes.detailsContainer}>
        <FormControl className={classes.descriptionContainer}>
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('form_builder.left_panel.description')}
            </Typography>
          </Box>
          <TextField
            error={formDescription.length === 0}
            value={formDescription}
            multiline
            className={modalClasses.formDescription}
            variant="outlined"
            placeholder="Write a short description"
            rows={4}
            onChange={onFormDescriptionChange}
          />
        </FormControl>
        <Box>
          <Box className={classes.titleContainer}>
            <Typography
              className={modalClasses.titleText}
              variant="h6"
              component="h2"
            >
              {t('field_forms.work_order_assignment')}
            </Typography>
            <IconButton aria-label="delete" onClick={helpTipHandler}>
              <InfoOutlinedIcon className={classes.infoIconStyle} />
            </IconButton>
          </Box>
          {showAssignmentTip && (
            <FormHelperText className={modalClasses.formHelperTextStyle}>
              {t('field_forms.assignment_tip')}
            </FormHelperText>
          )}
        </Box>
        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.trade')}
            </Typography>
          </Box>

          <Select
            error={!trade?.name}
            disabled={publishedMode}
            MenuProps={menuProps}
            className={modalClasses.selectStyle}
            value={trade?.name ?? ''}
            onChange={onTradeChange}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_trade')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {tradeList.map(trade => (
              <MenuItem
                key={`${trade?.id}-${trade?.name}`}
                value={trade}
                className={modalClasses.itemsFontStyle}
              >
                {trade?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.service')}
            </Typography>
          </Box>

          <Select
            error={!service?.name}
            MenuProps={menuProps}
            disabled={!trade || publishedMode}
            className={modalClasses.selectStyle}
            value={service?.name ?? ''}
            onChange={onServiceChange}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_service')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {serviceList.map(service => (
              <MenuItem
                key={`${service?.id}-${service?.name}`}
                value={service}
                className={modalClasses.itemsFontStyle}
              >
                {service?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={modalClasses.formControl} size="small">
          <Box className={modalClasses.titleContainer}>
            <Typography className={modalClasses.titleSymbol}>*</Typography>
            <Typography className={modalClasses.titleFontStyle}>
              {t('field_forms.column_names.type')}
            </Typography>
          </Box>

          <Select
            error={!woType?.name}
            disabled={publishedMode}
            MenuProps={menuProps}
            className={modalClasses.selectStyle}
            value={woType?.name ?? ''}
            onChange={onWoTypeChange}
            displayEmpty
            renderValue={selected => {
              if (!selected) {
                return (
                  <div className={modalClasses.itemsFontStyle}>
                    {t('field_forms.select_type')}
                  </div>
                )
              }

              return (
                <div className={modalClasses.itemsFontStyle}>{selected}</div>
              )
            }}
          >
            {typeList.map(woType => (
              <MenuItem
                key={`${woType?.id}-${woType?.name}`}
                value={woType}
                className={modalClasses.itemsFontStyle}
              >
                {woType?.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    )
  }

  return (
    <Box className={classes.leftPanelContainer}>
      <Box display="flex" className={classes.tabsContainer}>
        <Tabs
          value={leftTabValue}
          onChange={handleLeftTabChange}
          indicatorColor="primary"
          textColor="inherit"
          TabIndicatorProps={{
            className: classes.tabIndicator
          }}
        >
          <Tab
            disableRipple
            label={t('form_builder.left_panel.structure')}
            className={classes.textTabsStyles}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className={classes.tabsDivider}
          />
          <Tab
            disableRipple
            label={t('form_builder.left_panel.elements')}
            className={classes.textTabsStyles}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className={classes.tabsDivider}
          />
          <Tab
            disableRipple
            label={t('form_builder.left_panel.details')}
            className={classes.textTabsStyles}
          />
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            className={classes.tabsDivider}
          />
          <Tab
            disableRipple
            label={t('form_builder.left_panel.audit_trail')}
            className={classes.textTabsStyles}
          />
        </Tabs>
      </Box>
      <Box className={classes.panelsConstainer}>
        {/* Structure */}
        <TabPanel value={leftTabValue} index={0}>
          <p className={classes.structureMessage0}>
            {t('form_builder.left_panel.structure_tab_message_0')}
            <span className={classes.structureMessage1}>
              {t('form_builder.left_panel.structure_tab_message_1')}
            </span>
          </p>
          <StructureTab
            onAddSection={onAddSection}
            configurationsField={configurationsField}
            onEditSectionName={onEditSectionName}
            onRemoveSection={onRemoveSection}
            allDisabled={publishedMode}
          />
        </TabPanel>
        {/* Elements */}
        <TabPanel value={leftTabValue} index={2}>
          <Typography className={classes.addElementsMessage}>
            {t('form_builder.left_panel.select_drop_message')}
          </Typography>
          <Trash id={TRASH_ID}>
            {draggableComponents.map((item, index) => {
              const elementType = item[getFirstKeyFromObject(item)]?.type
              if (
                // disable if published
                publishedMode ||
                // disable elements based on config
                (elementsConfigs &&
                  Object.keys(elementsConfigs).includes(
                    `disable_${elementType}`
                  ) && elementsConfigs[`disable_${elementType}`] === true) ||
                // disable authorization field if already present
                (extraConditions.authorzationElement &&
                  elementType === elementTypes.AUTHORIZATION_FIELD)
              ) {
                return (
                  <Element
                    key={`key-${getFirstKeyFromObject(item)}`}
                    elementKey={elementType}
                    disabled
                  />
                )
              }
              /* custom elements after the divider keeping only one drag and drop list */
              if (index === elements.length) {
                if (publishedMode || disabledElements.includes(elementType)) {
                  // render this to disable the element
                  return (
                    <Fragment key={`${getFirstKeyFromObject(item)}_container`}>
                      <Divider key={`${getFirstKeyFromObject(item)}_divider`} />
                      <Element
                        key={`key-${getFirstKeyFromObject(item)}`}
                        elementKey={elementType}
                        disabled
                      />
                    </Fragment>
                  )
                }
                return (
                  <Fragment key={`${getFirstKeyFromObject(item)}_container`}>
                    <Divider key={`${getFirstKeyFromObject(item)}_divider`} />
                    <DraggableElement
                      key={getFirstKeyFromObject(item)}
                      item={item}
                      onClickAdd={onClickAdd}
                      allDisabled={publishedMode}
                    />
                  </Fragment>
                )
              }

              if (publishedMode || disabledElements.includes(elementType)) {
                // render this to disable the element
                return (
                  <Element
                    key={`key-${getFirstKeyFromObject(item)}`}
                    elementKey={elementType}
                    disabled
                  />
                )
              }
              return (
                <DraggableElement
                  key={getFirstKeyFromObject(item)}
                  item={item}
                  onClickAdd={onClickAdd}
                  allDisabled={publishedMode}
                />
              )
            })}
            {/* dummy elements */}
            <Element elementKey={elementTypes.EQUIPMENT_INVENTORY} disabled />
            <Divider />
          </Trash>
        </TabPanel>
        {/* Details */}
        <TabPanel value={leftTabValue} index={4}>
          {details()}
        </TabPanel>
        <TabPanel value={leftTabValue} index={6}>
          <AuditTrailTab />
        </TabPanel>
      </Box>
    </Box>
  )
}

export default LeftPanel
